/**
 * Onboarding: a name and a Jalali birth date — then straight to the
 * starting-point assessment, which asks the questions that fit the age.
 */

import { useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { Api } from '@/core/api';
import { getSurface } from '@/core/auth';
import { fa, JALALI_MONTHS, jalaliMonthLength, jalaliToIso, todayJalali } from '@/core/jalali';

export function NewProfilePage() {
  const navigate = useNavigate();
  const self = getSurface() === 'person';
  const t = todayJalali();
  const [name, setName] = useState('');
  const [jy, setJy] = useState(t.jy - 4);
  const [jm, setJm] = useState(1);
  const [jd, setJd] = useState(1);
  const [label, setLabel] = useState(self ? 'خودم' : 'مادر');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const years = Array.from({ length: 60 }, (_, i) => t.jy - i);
  const days = Array.from({ length: jalaliMonthLength(jy, jm) }, (_, i) => i + 1);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      const created = await Api.createProfile({
        displayName: name.trim(),
        dateOfBirth: jalaliToIso(jy, jm, jd),
        self,
        label,
      });
      await navigate({ to: '/p/$id/baseline', params: { id: created.id } });
    } catch (e) {
      setError('ثبت نشد. دوباره تلاش کنید.');
      console.error(e);
    } finally {
      setBusy(false);
    }
  };

  return (
    <main className="page">
      <h1 className="page-title">{self ? 'پروفایل من' : 'پروفایل فرزندم'}</h1>
      <section className="card">
        <label className="field">
          <span>{self ? 'نام' : 'نام فرزند'}</span>
          <input
            className="input"
            value={name}
            onChange={(e) => setName(e.target.value)}
            autoFocus
          />
        </label>
        <div className="field">
          <span>تاریخ تولد</span>
          <div className="row" style={{ flexWrap: 'nowrap' }}>
            <select
              className="input"
              value={jd}
              onChange={(e) => setJd(Number(e.target.value))}
              aria-label="روز"
            >
              {days.map((d) => (
                <option key={d} value={d}>
                  {fa(d)}
                </option>
              ))}
            </select>
            <select
              className="input"
              value={jm}
              onChange={(e) => setJm(Number(e.target.value))}
              aria-label="ماه"
            >
              {JALALI_MONTHS.map((m, i) => (
                <option key={m} value={i + 1}>
                  {m}
                </option>
              ))}
            </select>
            <select
              className="input"
              value={jy}
              onChange={(e) => setJy(Number(e.target.value))}
              aria-label="سال"
            >
              {years.map((y) => (
                <option key={y} value={y}>
                  {fa(y)}
                </option>
              ))}
            </select>
          </div>
        </div>
        {!self && (
          <label className="field">
            <span>شما چه نسبتی دارید؟</span>
            <select className="input" value={label} onChange={(e) => setLabel(e.target.value)}>
              {['مادر', 'پدر', 'مادربزرگ', 'پدربزرگ', 'سرپرست'].map((l) => (
                <option key={l}>{l}</option>
              ))}
            </select>
          </label>
        )}
        <p className="help">
          بعد از این، برای هر بخش چند سؤال ساده می‌پرسیم تا از جایی شروع کنیم که{' '}
          {self ? 'شما' : 'فرزندتان'} الان هست — نه از اول.
        </p>
        {error && <div className="error">{error}</div>}
        <button
          type="button"
          className="btn primary block"
          disabled={busy || name.trim().length === 0}
          onClick={submit}
        >
          ادامه به سؤال‌ها
        </button>
      </section>
    </main>
  );
}
